/**
 * Mentor Management API
 */

import { apiClient, ApiResponse, ApiError, ApiErrorResponse } from './client';

export interface MentorRequest {
  requestId: string;
  cohortId: string;
  cohortName: string;
  userId: string;
  userName: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  processedAt?: string;
  processedBy?: string;
  notes?: string;
}

export interface Mentor {
  mentorId: string;
  mentorName?: string;
  menteeCount: number;
  mentees: string[];
}

export interface MentorInfo {
  mentorId: string;
  assignedAt: string;
  assignedBy: string;
}

export interface MenteeWithProgress {
  userId: string;
  userName: string;
  progress: {
    completed: number;
    total: number;
    percentage: number;
  };
  pendingReviews: number;
}

export interface AssignmentPreviewRow {
  menteeId: string;
  menteeName: string;
  mentorId: string | null;
  mentorName: string | null;
  isNew: boolean;
}

export interface AssignmentPreviewResponse {
  preview: true;
  assignments: AssignmentPreviewRow[];
  mentors: { mentorId: string; mentorName: string }[];
  newAssignmentsCount: number;
}

export interface CreateMentorRequestData {
  cohortId: string;
  userId: string;
  notes?: string;
}

/**
 * Mentor API Service
 */
export const mentorsApi = {
  /**
   * Request to become a mentor for a cohort
   */
  async requestMentor(data: CreateMentorRequestData): Promise<MentorRequest> {
    const response = await apiClient.post<ApiResponse<MentorRequest>>(
      `/cohorts/${data.cohortId}/mentor-requests`,
      data
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data;
  },

  /**
   * Approve mentor request
   */
  async approveMentorRequest(requestId: string, notes?: string): Promise<void> {
    const response = await apiClient.put<ApiResponse<void>>(
      `/mentors/requests/${requestId}/approve`,
      { notes }
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Reject mentor request
   */
  async rejectMentorRequest(requestId: string, notes?: string): Promise<void> {
    const response = await apiClient.put<ApiResponse<void>>(
      `/mentors/requests/${requestId}/reject`,
      { notes }
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * List all mentors for a cohort
   */
  async listMentors(cohortId: string): Promise<Mentor[]> {
    const response = await apiClient.get<ApiResponse<{ mentors: Mentor[]; count: number }>>(
      `/cohorts/${cohortId}/mentors`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data.mentors;
  },

  /**
   * List all mentor requests for a cohort
   */
  async listMentorRequests(cohortId: string): Promise<MentorRequest[]> {
    const response = await apiClient.get<ApiResponse<{ requests: MentorRequest[]; count: number }>>(
      `/cohorts/${cohortId}/mentor-requests`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data.requests;
  },

  /**
   * Get the calling user's assigned mentor for a cohort
   */
  async getMentorForUser(cohortId: string): Promise<MentorInfo | null> {
    const response = await apiClient.get<ApiResponse<{ mentor: MentorInfo | null; message?: string }>>(
      `/cohorts/${cohortId}/mentor`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data.mentor;
  },

  /**
   * Get mentees for a mentor in a cohort
   */
  async getMentees(cohortId: string, mentorId: string): Promise<MenteeWithProgress[]> {
    const response = await apiClient.get<ApiResponse<{ mentees: MenteeWithProgress[]; count: number }>>(
      `/cohorts/${cohortId}/mentors/${mentorId}/mentees`
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse); return response.data.mentees;
  },

  async assignMentor(cohortId: string, mentorId: string, menteeId: string): Promise<void> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/cohorts/${cohortId}/mentors/${mentorId}/mentees`,
      { menteeId }
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  async removeMentor(cohortId: string, mentorId: string, menteeId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/cohorts/${cohortId}/mentors/${mentorId}/mentees/${menteeId}`
    );
    if (!response.success) {
      throw new ApiError(response as unknown as ApiErrorResponse);
    }
  },

  /**
   * Auto-assign unassigned participants to mentors using even distribution
   */
  async autoAssignMentors(cohortId: string): Promise<{ assignmentsCreated: number; message: string }> {
    const response = await apiClient.post<ApiResponse<{ assignmentsCreated: number; message: string }>>(
      `/cohorts/${cohortId}/mentors/auto-assign`,
      {}
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },

  /**
   * Preview auto-assignment without saving — returns full proposed assignment list
   */
  async previewAutoAssign(cohortId: string): Promise<AssignmentPreviewResponse> {
    const response = await apiClient.post<ApiResponse<AssignmentPreviewResponse>>(
      `/cohorts/${cohortId}/mentors/auto-assign?preview=true`,
      {}
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },

  /**
   * Save a full assignment list after admin reviews/edits the preview.
   * Handles both new assignments and unassignments (mentorId: null).
   * Uses Promise.allSettled so partial failures are reported rather than thrown.
   */
  async saveAssignments(
    cohortId: string,
    assignments: { menteeId: string; mentorId: string | null }[]
  ): Promise<{ failed: { menteeId: string; error: unknown }[] }> {
    const results = await Promise.allSettled(
      assignments.map(({ menteeId, mentorId }) => {
        const call = !mentorId
          ? apiClient.delete<ApiResponse<void>>(
              `/cohorts/${cohortId}/mentors/${menteeId}/assignment`
            )
          : apiClient.post<ApiResponse<void>>(
              `/cohorts/${cohortId}/mentors`,
              { mentorId, menteeId }
            );
        return call.then(resp => {
          if (!resp.success) throw new ApiError(resp as unknown as ApiErrorResponse);
          return resp;
        });
      })
    );

    const failed = results
      .map((r, i) =>
        r.status === 'rejected'
          ? { menteeId: assignments[i].menteeId, error: r.reason }
          : null
      )
      .filter((x): x is { menteeId: string; error: unknown } => x !== null);

    return { failed };
  },

  /**
   * Directly add a user as a mentor (admin action, skips request/approval)
   */
  async addMentor(cohortId: string, userId: string): Promise<{ message: string; mentor: { mentorId: string; mentorName: string } }> {
    const response = await apiClient.post<ApiResponse<{ message: string; mentor: { mentorId: string; mentorName: string } }>>(
      `/cohorts/${cohortId}/mentors/add`,
      { userId }
    );
    if (!response.success) throw new ApiError(response as unknown as ApiErrorResponse);
    return response.data;
  },
};
